import React, { Children, PureComponent } from 'react';
import isEqual from 'lodash.isequal';
import { ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { AbortableFetchEvent, FetchEvent } from '@philips-internal/oswald-ui-core/events';
import { DataContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { getLostProps, getPathParams, getResolvedPathAndParameters, isContentfulString, isDefined, isEqualShallow, isFunction, isObject, isPathReady, propagateData } from '@philips-internal/oswald-ui-utils';
import { FetchContextProvider } from './fetch-context.js';
import { SKIP_FETCH_SYMBOL } from './fetch-symbol.js';
import { FetchProperties } from './fetch-types.js';
const FetchContainer = {
    acceptedTypes: [
        ComponentType.BEHAVIOR,
        ComponentType.DATA_DISPLAY,
        ComponentType.DATA_ENTRY,
        ComponentType.FORM,
        ComponentType.LIST,
        ComponentType.NAVIGATION,
    ],
    name: '',
};
class Fetch extends PureComponent {
    constructor() {
        super(...arguments);
        this.defaultPagination = {};
        this.state = {
            abortController: null,
            accumulate: false,
            forceFetch: false,
            loading: false,
            params: {},
            previousParameters: {},
            [SKIP_FETCH_SYMBOL]: false,
            totalCount: null,
        };
        this.hasChildren = () => {
            const { children } = this.props;
            return Children.count(children) > 0;
        };
        this.isEndpointReady = () => {
            const { endpoint = FetchProperties.endpoint.defaultValue } = this.props;
            if (!isContentfulString(endpoint)) {
                return false;
            }
            const requestParameters = this.getRequestParameters(this.props, this.state);
            const { resolvedPath: resolvedEndpoint } = getResolvedPathAndParameters(endpoint, requestParameters);
            return isPathReady(resolvedEndpoint);
        };
        this.shouldFetch = (prevProps, prevState) => {
            const { children, endpoint = FetchProperties.endpoint.defaultValue } = this.props;
            const { forceFetch, page, previousParameters, reset } = this.state;
            const requestParameters = this.getRequestParameters(this.props, this.state);
            const isEndpointReady = this.isEndpointReady();
            if (reset) {
                this.setState({
                    reset: false,
                });
            }
            if (forceFetch) {
                this.setState({
                    forceFetch: false,
                });
                return isEndpointReady;
            }
            if (this.state[SKIP_FETCH_SYMBOL]) {
                return false;
            }
            if (prevState.page !== page) {
                return isEndpointReady;
            }
            if (!isEqualShallow(previousParameters, requestParameters)) {
                return isEndpointReady;
            }
            if (prevProps.endpoint !== endpoint || prevProps.children !== children) {
                return isEndpointReady;
            }
            return false;
        };
        this.fetch = () => {
            if (this.state.abortController) {
                this.state.abortController.abort();
            }
            if (!this.hasChildren() || !this.isEndpointReady()) {
                return;
            }
            const { endpoint = FetchProperties.endpoint.defaultValue, onBeforeFetch } = this.props;
            const requestParameters = this.getRequestParameters(this.props, this.state);
            const abortController = new AbortController();
            this.setState({
                abortController,
                loading: true,
                previousParameters: requestParameters,
            }, () => {
                const abort = () => abortController.abort();
                isFunction(onBeforeFetch) && onBeforeFetch(new AbortableFetchEvent({ abort }));
                HttpService.get(endpoint, requestParameters, { abortController })
                    .then(this.handleSuccess)
                    .catch(this.handleError);
            });
        };
        this.handleSuccess = ({ body, headers }) => {
            if (this.aborted) {
                return;
            }
            const { onAfterFetch, totalCountHeader = FetchProperties.totalCountHeader.defaultValue } = this.props;
            this.setState((prevState) => {
                const { accumulate = this.defaultPagination?.accumulate, limit = this.defaultPagination?.limit, data: prevData, } = prevState;
                const data = this.getData(prevData, body, accumulate);
                const done = Array.isArray(data) && Array.isArray(body) && limit ? body.length < limit : true;
                const totalCount = Number(headers.get(totalCountHeader));
                return {
                    abortController: null,
                    data,
                    done,
                    loading: false,
                    totalCount,
                };
            }, () => {
                const { data } = this.state;
                isFunction(onAfterFetch) && onAfterFetch(new FetchEvent({ data }));
            });
        };
        this.handleError = (error) => {
            if (this.aborted || (isDefined(error.code) && error.code === error.ABORT_ERR)) {
                return;
            }
            const { onAfterFetch } = this.props;
            this.setState({
                abortController: null,
                error: error.statusText,
                loading: false,
            }, () => {
                isFunction(onAfterFetch) && onAfterFetch(new FetchEvent({ error: new Error(error.statusText) }));
            });
        };
        this.onPageChange = (options = {}) => {
            const { accumulate = false, limit, mergeParams = false, page, params: newParams = {}, reset, skip = false, } = options;
            this.setState(prevState => {
                const { data: oldData, params: oldParams, previousParameters: oldPreviousParameters } = prevState;
                const params = mergeParams ? { ...oldParams, ...newParams } : newParams;
                const data = reset ? [] : oldData;
                const previousParameters = reset ? {} : oldPreviousParameters;
                return {
                    accumulate,
                    data,
                    limit,
                    page,
                    params,
                    previousParameters,
                    [SKIP_FETCH_SYMBOL]: skip,
                };
            });
        };
        this.onSort = (options) => {
            const { direction, name: entry, limit, params } = options;
            const parameters = {
                ...params,
                direction,
                entry,
            };
            this.setState({
                accumulate: false,
                limit,
                page: 0,
                params: parameters,
                [SKIP_FETCH_SYMBOL]: false,
            });
        };
        this.setInitialPagination = (options) => {
            this.defaultPagination = options;
        };
    }
    get aborted() {
        const { abortController } = this.state;
        return abortController?.signal.aborted ?? false;
    }
    get sizeParameters() {
        return ['size'];
    }
    componentDidMount() {
        this.fetchLazy();
    }
    componentDidUpdate(previousProps, previousState) {
        const { parameters } = this.props;
        const { page, params } = this.state;
        if (isDefined(page) && page > 0 && (!isEqual(previousProps.parameters, parameters) || !isEqual(previousState.params, params))) {
            this.setState({
                accumulate: false,
                page: 0,
            });
        }
        if (this.shouldFetch(previousProps, previousState)) {
            this.fetch();
        }
    }
    componentWillUnmount() {
        const { abortController } = this.state;
        abortController && abortController.abort();
    }
    getCount() {
        const { data } = this.state;
        if (isObject(data)) {
            const keys = Object.keys(data);
            const key = keys.find((key) => this.sizeParameters.includes(key));
            if (key) {
                return Number(data[key]);
            }
        }
        return data ? Number(data.length) : null;
    }
    getParametersByName(names = [], parameters = {}) {
        const { dependeeData = {} } = this.props;
        const source = {
            ...dependeeData,
            ...parameters,
        };
        return names.reduce((result, name) => {
            result[name] = source[name];
            return result;
        }, {});
    }
    getRequestParameters(props, state) {
        const { endpoint = FetchProperties.endpoint.defaultValue, parameters = {} } = props;
        const { limit, page, params = {} } = { ...this.defaultPagination, ...state };
        const names = getPathParams(endpoint);
        const context = (this.context || {});
        const paramsFromContext = this.getParametersByName(names, context);
        const requestParams = {
            ...parameters,
            ...params,
        };
        isDefined(page) && (requestParams.page = page);
        isDefined(limit) && (requestParams.limit = limit);
        return { ...paramsFromContext, ...requestParams };
    }
    getData(prevData, data, accumulate = false) {
        if (!isDefined(prevData) || !accumulate) {
            return data;
        }
        return Array.isArray(data) && Array.isArray(prevData)
            ? [...prevData, ...data]
            : { ...prevData, ...data };
    }
    fetchLazy() {
        const { lazy } = this.props;
        !lazy && this.fetch();
    }
    render() {
        const { children, endpoint = FetchProperties.endpoint.defaultValue, lazy, uuid } = this.props;
        const { accumulate, data, done, loading, page, reset, totalCount } = this.state;
        const lostProps = getLostProps(FetchProperties, this.props);
        if (Children.count(children) === 0) {
            return React.createElement(Container, { acceptedTypes: FetchContainer.acceptedTypes, className: "u-expand", uuid: uuid });
        }
        const inherited = {
            ...lostProps,
            accumulate,
            count: this.getCount(),
            done: isDefined(done) ? done : lazy,
            endpoint,
            loading,
            onNextPage: this.onPageChange,
            onPrevPage: this.onPageChange,
            onSort: this.onSort,
            page,
            reset: reset ?? lostProps.reset,
            totalCount,
        };
        return (React.createElement(FetchContextProvider, { value: { setInitialPagination: this.setInitialPagination } }, propagateData(children, data, inherited)));
    }
}
Fetch.contextType = DataContext.Context;
export { Fetch, FetchContainer, };
//# sourceMappingURL=fetch.js.map